import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import styles from './login.module.css';
import api from '../../utils/api';
import Toastify from '../../utils/Toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const defaultLoginSlides = [
    {
        image: '/assets/login.png',
        title: 'Thomas Edison',
        description: 'From pioneering electric light to shaping the future of innovation.',
    },
    {
        image: '/assets/login-slide-components.png',
        title: 'Built for precision',
        description: 'Source the right electronic components with clarity and confidence.',
    },
    {
        image: '/assets/login-slide-precision.png',
        title: 'Ideas into production',
        description: 'Bring every design to life with reliable sourcing in one place.',
    },
];

const getLoginSlides = (loginBanner) => {
    const managedSlides = loginBanner?.slides?.filter((slide) => slide?.image);

    if (managedSlides?.length) {
        return managedSlides;
    }

    return [
        { ...defaultLoginSlides[0], image: loginBanner?.image || defaultLoginSlides[0].image },
        ...defaultLoginSlides.slice(1),
    ];
};

const Login = () => {
    const { appearences, loading } = useSelector((state) => state.appearences)
    const location = useLocation()
    const from = location.state
    const [passwordVisible, setPasswordVisible] = useState(false);
    const navigate = useNavigate()

    // Links in emails open /login?redirect=/some/page so the customer lands on that page after logging in.
    // Only paths inside this site are accepted.
    const redirectTo = (() => {
        const target = new URLSearchParams(location.search).get('redirect') || '';
        return target.startsWith('/') && !target.startsWith('//') && !target.includes('://') ? target : '';
    })();

    // Already logged in: go straight to the page from the email
    useEffect(() => {
        if (redirectTo && localStorage.getItem('token')) navigate(redirectTo, { replace: true });
    }, [redirectTo, navigate]);
    const [isLoding, setIsloading] = useState(false)
    const [backgroundSlides, setBackgroundSlides] = useState(defaultLoginSlides);
    const [activeSlide, setActiveSlide] = useState(0);
    const touchStart = useRef(null);

    useEffect(() => {
        const reason = sessionStorage.getItem("logoutReason");
        if (reason === "passwordChanged") {
            Toastify.warning("Logged Out", "You have been logged out because your password was changed on another device. Please log in again.");
            sessionStorage.removeItem("logoutReason");
        }
    }, []);

    useEffect(() => {
        setBackgroundSlides(getLoginSlides(appearences?.loginBanner));
    }, [appearences?.loginBanner]);

    useEffect(() => {
        setActiveSlide(0);
    }, [backgroundSlides]);

    useEffect(() => {
        const slider = setInterval(() => {
            setActiveSlide((currentSlide) => (currentSlide + 1) % backgroundSlides.length);
        }, 7500);

        return () => clearInterval(slider);
    }, [backgroundSlides.length]);

    const moveSlide = (direction) => {
        setActiveSlide((currentSlide) => {
            const nextSlide = currentSlide + direction;
            return (nextSlide + backgroundSlides.length) % backgroundSlides.length;
        });
    };

    const handleTouchStart = (event) => {
        const touch = event.touches[0];
        touchStart.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (event) => {
        if (!touchStart.current) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStart.current.x;
        const deltaY = touch.clientY - touchStart.current.y;
        touchStart.current = null;

        // Leave normal vertical scrolling alone; only intentional horizontal swipes change slides.
        if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        moveSlide(deltaX < 0 ? 1 : -1);
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    // Formik form handling and validation
    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email('Invalid email address')
                .required('Email is required'),
            password: Yup.string()
                .required('Password is required')
        }),
        onSubmit: async (values) => {
            try {
                setIsloading(true)
                const response = await api.login(values);
                if (response.data) {
                    Toastify.success("Logged in")

                    localStorage.setItem("token", response.data?.data?.token);
                    if (redirectTo) {
                        navigate(redirectTo, { replace: true })
                    } else if(from == "popup"){
                        navigate(-1)
                    }else{
                        navigate('/')
                    }
                }
            } catch (err) {
                console.log('Login Error', err);
                Toastify.error(err?.response?.data?.message)
            } finally {
                setIsloading(false)
            }
        }
    });

    return (
        <>
            <div className={styles.loginContainer} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                <div className={styles.backgroundCarousel} aria-hidden="true">
                    {backgroundSlides.map((slide, index) => (
                        <div
                            className={`${styles.backgroundSlide} ${index === activeSlide ? styles.backgroundSlideActive : ''}`}
                            key={slide.image}
                            style={{ backgroundImage: `url("${slide.image}")` }}
                        />
                    ))}
                </div>
                <div className={styles.loginInner}>

                    <div className={styles.loginCol1}>
                        <img src='/assets/logo.png' alt="Logo" />
                        <div className={styles.loginSlideCopy}>
                            <p className={styles.loginSlideTitle}>{backgroundSlides[activeSlide]?.title}</p>
                            <p className={styles.loginSlideDescription}>{backgroundSlides[activeSlide]?.description}</p>
                        </div>
                        <div className={styles.slideIndicators} aria-hidden="true">
                            {backgroundSlides.map((slide, index) => (
                                <span
                                    className={`${styles.slideIndicator} ${index === activeSlide ? styles.slideIndicatorActive : ''}`}
                                    key={`${slide.image}-indicator`}
                                />
                            ))}
                        </div>
                    </div>
                    <div className={styles.loginCol2}>
                        <div className={styles.loginCard}>
                            <h3>Login</h3>
                            <div className={styles.loginContent}>
                                <form className={styles.loginForm} onSubmit={formik.handleSubmit}>
                                    <div className={styles.loginInputGroup}>
                                        <label>Email Address</label>
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="Add Email Address"
                                            value={formik.values.email}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            required
                                        />
                                        {formik.touched.email && formik.errors.email ? (
                                            <div className={styles.error}>{formik.errors.email}</div>
                                        ) : null}
                                    </div>
                                    <div className={styles.loginInputGroup}>
                                        <label>Password</label>
                                        <div className={styles.passwordContainer}>
                                            <input
                                                type={passwordVisible ? 'text' : 'password'}
                                                name="password"
                                                placeholder="Enter Password"
                                                value={formik.values.password}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                className={styles.passwordInput}
                                                required
                                            />
                                            <FontAwesomeIcon
                                                icon={passwordVisible ? faEyeSlash : faEye}
                                                className={styles.passwordIcon}
                                                onClick={togglePasswordVisibility}
                                            />
                                        </div>
                                        <span className={styles.forgotPassword} onClick={() => navigate('/forgotPassword')} style={{ cursor: 'pointer' }}>Forgot password?</span>
                                    </div>
                                    <button type="submit" className={styles.loginButton} disabled={isLoding} >
                                        {isLoding ? 'Please wait...' : 'Log In'}
                                    </button>
                                </form>
                                <div className={styles.loginAccLinks}>
                                    <div className={styles.loginSeparator} >
                                        <div className={styles.separator}></div>
                                        <p>New to ElectroWiz?</p>
                                        <div className={styles.separator}></div>
                                    </div>
                                    <button className={styles.loginCreateAccBtn} onClick={() => navigate('/register')}>
                                        Create an account
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>

    );
}

export default Login;
